import ApiService from '@shared/services/api.service';

const IncidentService = {
  fetchList: (page, itemsPerPage, querySearch, fromDate, toDate, userId = null, tagIds = [], severityLevels = [], httpStatusCodes = [], exact = false) => {
    let url = `/incidents?page=${page}&items_per_page=${itemsPerPage}&q=${encodeURIComponent(querySearch || '')}`;
    if (exact) url += `&exact=true`;
    if (userId) url += `&user_id=${userId}`;
    if (fromDate && toDate) url += `&start_date=${fromDate}&end_date=${toDate}`;
    if (Array.isArray(tagIds) && tagIds.length > 0) {
      url += `&` + tagIds.map(id => `tag_ids[]=${encodeURIComponent(id)}`).join('&');
    }
    if (Array.isArray(severityLevels) && severityLevels.length > 0) {
      url += `&` + severityLevels.map(level => `severity_levels[]=${encodeURIComponent(level)}`).join('&');
    }
    if (Array.isArray(httpStatusCodes) && httpStatusCodes.length > 0) {
      url += `&` + httpStatusCodes.map(code => `http_status_codes[]=${encodeURIComponent(code)}`).join('&');
    }
    return ApiService.get(url);
  },
  fetchFullList: (fromDate, toDate) => {
    return ApiService.get(`/incidents/get_full_list?start_date=${fromDate}&end_date=${toDate}`);
  },
  fetchExportList: (page, itemsPerPage, querySearch, fromDate, toDate) => {
    return ApiService.get(`/incidents/get_export_list?page=${page}&items_per_page=${itemsPerPage}&q=${encodeURIComponent(querySearch || '')}&start_date=${fromDate}&end_date=${toDate}`);
  },
  fetchAllowedPeriod: () => {
    return ApiService.get(`/incidents/get_allowed_period`);
  },
  fetchDetailIncident: (id) => ApiService.get(`/incidents/${id}`),
  /**
   * `GET /incidents/{id}/aggregation` — the aggregated alert this one alert was
   * put in, and the site-wide incident that group belongs to.
   *
   * Both parts can be null, and that is an ordinary answer: most alerts are not
   * aggregated. There is no batch form of this endpoint, so it must never be
   * called once per row of a list.
   * @param {string|number} id an alert id
   * @returns {Promise<{change_event: object|null, site_incident: object|null}>}
   */
  fetchAggregation: (id) => ApiService.get(`/incidents/${id}/aggregation`),
  /**
   * The grouping of a whole page of alerts, in one request.
   *
   * The alert list draws a hundred rows; one request per row is not an option.
   * Returns an object keyed by alert id, holding only the alerts that are in a
   * group, so a missing key means "not grouped", not "failed".
   * @param {Array<number>} ids
   */
  fetchAggregations: async (ids) => {
    // The query string is built here on purpose: ApiService.get takes a cancel
    // token as its second argument, NOT an axios config, so passing { params }
    // silently sends no parameters at all.
    //
    // Sent in chunks: the page size goes up to 200, and 200 ids make a query
    // string of about 2.6 KB, above what some proxies accept. Each chunk
    // returns an object keyed by alert id, so the pieces merge with no
    // overlap.
    const CHUNK = 50;
    const chunks = [];
    for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));
    const results = await Promise.all(chunks.map(chunk => {
      const query = chunk.map(id => `ids[]=${encodeURIComponent(id)}`).join('&');
      return ApiService.get(`/incidents/aggregations?${query}`);
    }));
    // The API answers a bare [] when none of the ids are grouped.
    return Object.assign({}, ...results.filter(part => part && typeof part === 'object' && !Array.isArray(part)));
  },
  fetchScreenShot: (id) => (ApiService.get(`/incidents/${id}/screenshots`)),
  reBaselineWebpage: (urlId) => ApiService.post(`/urls/${urlId}/rebaseline`),
  fetchFilesToCompare: (incidentId) => ApiService.get(`/incidents/${incidentId}/files`),
  fetchUrlFileToDownload: (incidentId) => ApiService.get(`incidents/${incidentId}/get_download_difference_url`),
  fetchDetailVerify: async (id) => {
    return ApiService.get(`/verify_records/${id}`);
  }
};

export default IncidentService;
